/**
 * Utility functions for detecting and handling AngularJS templates
 */

/**
 * Checks if a template string contains AngularJS directives or expressions
 */
export const isAngularJSTemplate = (template: string): boolean => {
    if (!template || typeof template !== 'string') {
        return false;
    }

    // Check for AngularJS directives
    const angularDirectives = [
        'data-ng-',
        'ng-',
        'x-ng-',
        'ng:',
        'ng_'
    ];

    // Check for AngularJS expressions
    const angularExpressions = [
        '{{',
        '}}',
        '{{::',
        '{{=',
        '{{#',
        '{{/'
    ];

    // Check for AngularJS filters
    const angularFilters = [
        '| filter:',
        '| orderBy:',
        '| limitTo:',
        '| date:',
        '| currency:',
        '| number:',
        '| json:'
    ];

    const hasDirectives = angularDirectives.some(directive => template.includes(directive));
    const hasExpressions = angularExpressions.some(expression => template.includes(expression));
    const hasFilters = angularFilters.some(filter => template.includes(filter));

    return hasDirectives || hasExpressions || hasFilters;
};

/**
 * Extracts AngularJS scope variables from a template
 */
export const extractScopeVariables = (template: string): string[] => {
    if (!isAngularJSTemplate(template)) {
        return [];
    }

    const variables: string[] = [];

    // Extract variables from expressions like {{variable}}
    const expressionRegex = /\{\{([^}]+)\}\}/g;
    let match;
    while ((match = expressionRegex.exec(template)) !== null) {
        const expression = match[1].trim();
        // Split by operators and filters to get variable names
        const parts = expression.split(/[\\|\\+\-\\*\\/\s]+/);
        parts.forEach(part => {
            const cleanPart = part.trim();
            if (cleanPart && /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(cleanPart)) {
                variables.push(cleanPart);
            }
        });
    }

    // Extract variables from ng-model and other directives
    const directiveRegex = /(?:data-)?ng-(?:model|bind|if|show|hide|repeat|click|change|submit)\s*=\s*["']([^"']+)["']/g;
    while ((match = directiveRegex.exec(template)) !== null) {
        const variable = match[1].trim();
        if (variable && /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(variable)) {
            variables.push(variable);
        }
    }

    return [...new Set(variables)]; // Remove duplicates
};
